import React, { useState, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, ArrowLeftRight, PackageSearch } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useRegion } from '../context/RegionContext';
import { FlagImg } from './FlagImg';

const Header = styled.header`
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 50;
  transition: all 0.4s cubic-bezier(0.16,1,0.3,1);

  ${({ $scrolled }) =>
    $scrolled
      ? css`
          background: rgba(255,253,247,0.96);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          box-shadow: 0 1px 0 rgba(26,18,8,0.07), 0 4px 24px rgba(26,18,8,0.06);
        `
      : css`
          background: transparent;
        `}
`;

const Nav = styled.nav`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  @media (min-width: 1024px) {
    padding: 0 32px;
    gap: 24px;
  }
`;

const LogoWrap = styled(Link)`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  background: ${({ $scrolled }) => ($scrolled ? '#c2824eff' : 'transparent')};
  border-radius: 0 0 10px 10px;
  padding: ${({ $scrolled }) => ($scrolled ? '45px 14px 12px' : '40px 0 0 0')};
  box-shadow: ${({ $scrolled }) => ($scrolled ? '0 4px 16px rgba(26, 18, 8, 0.32)' : 'none')};
  transition: background 0.4s, padding 0.4s, box-shadow 0.4s;
  &:hover {
    box-shadow: ${({ $scrolled }) => ($scrolled ? '0 6px 24px rgba(26,18,8,0.16)' : 'none')};
  }
  img {
    height: 120px;
    width: auto;
    object-fit: contain;
    display: block;
    filter: ${({ $scrolled, $light }) =>
      !$scrolled && !$light
        ? 'drop-shadow(0 0 8px rgba(255,253,247,0.7)) drop-shadow(0 0 2px rgba(255,253,247,1))'
        : 'none'};
    transition: filter 0.4s;
  }
`;

const DesktopLinks = styled.div`
  display: none;
  align-items: center;
  gap: 28px;
  @media (min-width: 1024px) { display: flex; }
`;

const NavLink = styled(Link)`
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ $scrolled, $active, $light }) =>
    $active
      ? $scrolled ? '#7C3A00' : $light ? '#7C3A00' : '#FFFFFF'
      : $scrolled
      ? 'rgba(26,18,8,0.65)'
      : $light
      ? 'rgba(26,18,8,0.62)'
      : 'rgba(255,253,247,0.96)'};
  transition: color 0.2s;
  position: relative;
  padding-bottom: 2px;
  &::after {
    content: '';
    position: absolute;
    bottom: -2px; left: 0;
    width: ${({ $active }) => ($active ? '100%' : '0')};
    height: 1px;
    background: ${({ $scrolled, $light }) => ($scrolled || $light ? '#7C3A00' : '#FFFFFF')};
    transition: width 0.25s ease;
  }
  &:hover {
    color: ${({ $scrolled, $light }) => ($scrolled || $light ? '#7C3A00' : '#FFFFFF')};
    &::after { width: 100%; }
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  @media (min-width: 1024px) {
    gap: 14px;
  }
`;

const HideOnMobile = styled.div`
  display: none;
  @media (min-width: 1024px) { display: flex; align-items: center; }
`;

const LangToggle = styled.div`
  display: flex;
  align-items: center;
  background: ${({ $scrolled, $light }) =>
    $scrolled ? '#FAF3E0' : $light ? 'rgba(26,18,8,0.07)' : 'rgba(255,253,247,0.15)'};
  border: 1px solid ${({ $scrolled, $light }) =>
    $scrolled ? '#E8DFD0' : $light ? 'rgba(26,18,8,0.18)' : 'rgba(255,253,247,0.35)'};
  border-radius: 999px;
  padding: 3px;
  transition: background 0.4s, border-color 0.4s;
`;

const LangBtn = styled.button`
  padding: 5px 14px;
  border-radius: 999px;
  font-weight: 600;
  letter-spacing: 0.05em;
  font-size: 0.75rem;
  transition: all 0.2s;
  color: ${({ $active, $scrolled, $light }) =>
    $active ? '#FFFDF7' : $scrolled ? 'rgba(26,18,8,0.55)' : $light ? 'rgba(26,18,8,0.55)' : 'rgba(255,253,247,0.90)'};
  background: ${({ $active }) => ($active ? '#7C3A00' : 'transparent')};
`;

const FlagBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 1.3rem;
  line-height: 1;
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px 6px;
  border-radius: 8px;
  color: ${({ $scrolled, $light }) =>
    $scrolled || $light ? 'rgba(26,18,8,0.5)' : 'rgba(255,253,247,0.7)'};
  transition: transform 0.15s, color 0.2s;
  &:hover {
    transform: scale(1.08);
    color: ${({ $scrolled, $light }) =>
      $scrolled || $light ? 'rgba(26,18,8,0.9)' : 'rgba(255,253,247,1)'};
  }
`;


const hintBounce = keyframes`
  0%, 100% { transform: translateY(0) translateX(-50%); }
  50%       { transform: translateY(-4px) translateX(-50%); }
`;
const hintFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-6px) translateX(-50%); }
  to   { opacity: 1; transform: translateY(0)    translateX(-50%); }
`;
const hintPulse = keyframes`
  0%   { box-shadow: 0 0 0 0   rgba(192,112,32,0.8); }
  70%  { box-shadow: 0 0 0 10px rgba(192,112,32,0); }
  100% { box-shadow: 0 0 0 0   rgba(192,112,32,0); }
`;

/* Envuelve el FlagBtn para poder poner el tooltip con position:absolute */
const FlagBtnWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const HintBubble = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  background: #1A1208;
  color: #FFFDF7;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 7px 13px;
  border-radius: 20px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 300;
  animation: ${hintFadeIn} 0.35s ease, ${hintBounce} 1s 0.35s ease infinite;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-bottom-color: #1A1208;
  }
`;


const MobileMenuBtnWrap = styled.div`
  position: relative;
  display: inline-flex;
  @media (min-width: 1024px) { display: none; }
`;

/* Punto naranja pulsante encima del botón */
const PulseDot = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #C07020;
  animation: ${hintPulse} 1.2s ease infinite;
  pointer-events: none;
`;

const MobileMenuBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  color: ${({ $scrolled, $light }) => ($scrolled ? '#1A1208' : $light ? '#1A1208' : '#FFFDF7')};
  transition: color 0.3s;
  @media (min-width: 1024px) { display: none; }
`;

const MobileMenu = styled.div`
  position: absolute;
  top: 100%; left: 0; right: 0;
  background: rgba(255,253,247,0.98);
  backdrop-filter: blur(16px);
  border-top: 1px solid rgba(232,223,208,0.6);
  box-shadow: 0 16px 40px rgba(26,18,8,0.1);
  @media (min-width: 1024px) { display: none; }
`;

const MobileLinks = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 20px 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MobileLink = styled(Link)`
  font-size: 1rem;
  font-weight: 400;
  color: ${({ $active }) => ($active ? '#7C3A00' : 'rgba(26,18,8,0.75)')};
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  padding: 10px 0;
  border-bottom: 1px solid rgba(232,223,208,0.4);
  transition: color 0.2s, padding-left 0.2s;
  &:last-child { border-bottom: none; }
  &:hover { color: #7C3A00; padding-left: 6px; }
`;

const navLinks = (region) => [
  { to: `/${region}`,         en: 'Home',     es: 'Inicio',   fr: 'Accueil'  },
  { to: `/${region}/about`,   en: 'About Us', es: 'Nosotros', fr: 'À Propos' },
  { to: `/${region}/store`,   en: 'Store',    es: 'Tienda',   fr: 'Boutique' },
  { to: `/${region}/contact`, en: 'Contact',  es: 'Contacto', fr: 'Contact'  },
];

export function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const { region, switchRegion, showRegionHint } = useRegion();

  const REGIONS = [
    { code: 'mx', flag: '🇲🇽', en: 'México',        es: 'México',        fr: 'Mexique'       },
    { code: 'us', flag: '🇺🇸', en: 'United States', es: 'Estados Unidos', fr: 'États-Unis'   },
    { code: 'ca', flag: '🇨🇦', en: 'Canada',         es: 'Canadá',        fr: 'Canada'        },
  ];
  const regionLabel = lang === 'es' ? 'Según tu región' : lang === 'fr' ? 'Selon votre région' : 'Based on your region';
  const { totalItems } = useCart();
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const location = useLocation();

  const isHome = location.pathname === `/${region}` || location.pathname === '/';

  // Activo: match exacto para home, startsWith para el resto
  const isActive = (link) => {
    if (link.to === `/${region}`) return location.pathname === link.to;
    return location.pathname.startsWith(link.to);
  };

  const links = navLinks(region);
  const transparent = !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Reset scroll position on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    setMenuOpen(false);
  }, [location.pathname]);


  return (
    <Header $scrolled={!transparent}>
      <Nav>
        <LogoWrap to="/" $scrolled={!transparent} $light={!isHome}>
          <img src="/images/logonew.png" alt="Hacienda Real" />
        </LogoWrap>

        <DesktopLinks>
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              $scrolled={!transparent}
              $light={!isHome}
              $active={isActive(link)}
            >
              {t(link.en, link.es, link.fr)}
            </NavLink>
          ))}
        </DesktopLinks>

        <Controls>
          <FlagBtnWrap>
            <FlagBtn
              id="region-flag-btn"
              $scrolled={!transparent} $light={!isHome}
              onClick={switchRegion}
              title={lang === 'es' ? `Cambiar región (${REGIONS.find(r=>r.code===region)?.es})` : lang === 'fr' ? `Changer de région (${REGIONS.find(r=>r.code===region)?.fr})` : `Switch region (${REGIONS.find(r=>r.code===region)?.en})`}
              aria-label="Cambiar región"
            >
              <FlagImg code={region} size={18} />
              <ArrowLeftRight size={12} strokeWidth={2.2} />
            </FlagBtn>
            {showRegionHint && (
              <>
                <PulseDot />
                <HintBubble>
                  {lang === 'es' ? '¡Cambia región aquí!' : lang === 'fr' ? 'Changez de région ici !' : 'Switch region here!'}
                </HintBubble>
              </>
            )}
          </FlagBtnWrap>
          <HideOnMobile>
            <LangToggle $scrolled={!transparent} $light={!isHome}>
              <LangBtn $active={lang === 'en'} $scrolled={!transparent} $light={!isHome} onClick={() => setLang('en')}>EN</LangBtn>
              <LangBtn $active={lang === 'es'} $scrolled={!transparent} $light={!isHome} onClick={() => setLang('es')}>ES</LangBtn>
              <LangBtn $active={lang === 'fr'} $scrolled={!transparent} $light={!isHome} onClick={() => setLang('fr')}>FR</LangBtn>
            </LangToggle>
          </HideOnMobile>
          <Link to={`/${region}/cart`} aria-label="Ver carrito" style={{ textDecoration: 'none' }}>
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 4 }}>
              <ShoppingCart size={26} color={isHome && transparent ? '#FFFDF7' : '#1A1208'} />
              {totalItems > 0 && (
                <span style={{ position: 'absolute', top: -8, right: -8, background: '#C07020', color: '#fff', fontSize: 11, padding: '2px 6px', borderRadius: 999 }}>{totalItems}</span>
              )}
            </div>
          </Link>
          <Link
            to={`/${region}/track`}
            aria-label={lang === 'es' ? 'Rastrear pedido' : 'Track order'}
            title={lang === 'es' ? 'Rastrear pedido' : 'Track order'}
            style={{ display:'flex', alignItems:'center' }}
          >
            <PackageSearch size={26} color={isHome && transparent ? '#FFFDF7' : '#1A1208'} />
          </Link>
          <MobileMenuBtnWrap>
            <MobileMenuBtn id="mobile-menu-btn" $scrolled={!transparent} $light={!isHome} onClick={() => setMenuOpen((o) => !o)} aria-label="Toggle menu">
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </MobileMenuBtn>
          </MobileMenuBtnWrap>
        </Controls>
      </Nav>

      {menuOpen && (
        <MobileMenu>
          <MobileLinks>
            {links.map((link) => (
              <MobileLink
                key={link.to}
                to={link.to}
                $active={isActive(link)}
                onClick={() => setMenuOpen(false)}
              >
                {t(link.en, link.es, link.fr)}
              </MobileLink>
            ))}
            <div style={{ display: 'flex', gap: 10, padding: '12px 0 4px', alignItems: 'center', flexWrap: 'wrap' }}>
              <LangToggle $scrolled $light>
                <LangBtn $active={lang === 'en'} $scrolled $light onClick={() => setLang('en')}>EN</LangBtn>
                <LangBtn $active={lang === 'es'} $scrolled $light onClick={() => setLang('es')}>ES</LangBtn>
                <LangBtn $active={lang === 'fr'} $scrolled $light onClick={() => setLang('fr')}>FR</LangBtn>
              </LangToggle>
            </div>
          </MobileLinks>
        </MobileMenu>
      )}
    </Header>
  );
}
